import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import {
  DocumentsChangesRequest,
  DocumentsSPReponse,
} from "../../modules/documents/components/document.model";
import { HttpService } from "../http/http.service";
import { APIResponse } from "../http/models/api-response";

@Injectable({
  providedIn: "root",
})
export class DocumentsService {
  private readonly MODULE: string = "";
  private readonly VERSION: string = "v1";
  private readonly RESOURCE: string = "Document";
  private readonly GET_DOCUMENTS: string = "list";
  private readonly SUBMIT_CHANGES: string = "set-active";
  private readonly APPROVE_REJECT: string = "approve-reject";
  private readonly DELETE: string = "delete";
  private readonly UPLOAD_DOCUMENTS: string = "add-or-update";
  private readonly SYNCHRONIZE_DOCUMENTS: string = "synchronize-documents";

  private readonly HTTP_OPTIONS = {
    headers: new HttpHeaders({
      "Content-type": "application/json",
    }),
  };

  constructor(private http: HttpClient, private httpService: HttpService) { }

  getDocuments(
    draw: number = 1,
    start: number = 0,
    length: number = 10,
    searchValue: string = "",
    sortColumn: string = "",
    sortDirection: string = "asc",
    onlyPendingDocuments: boolean = false,
    pendingByDefault: boolean = false
  ): Observable<{
    data: DocumentsSPReponse[];
    totalCount: number;
    filteredCount: number;
    draw: number;
  }> {
    const params: any = {
      draw: draw,
      start: start,
      length: length,
      searchValue,
      sortDirection,
      onlyPendingDocuments: onlyPendingDocuments,
      pendingByDefault: pendingByDefault,
    };

    if (sortColumn) params.sortColumn = sortColumn;

    const httpOptions = {
      params,
      headers: new HttpHeaders({
        "Skip-Loading": "true",
      }),
    };

    return this.http
      .get<APIResponse>(
        this.httpService.buildUrl(
          this.MODULE,
          this.VERSION,
          this.RESOURCE,
          this.GET_DOCUMENTS
        ),
        httpOptions
      )
      .pipe(
        map((APIResponse: APIResponse) => {
          if (APIResponse.status.success)
            return {
              data: APIResponse.data.data,
              totalCount: APIResponse.data.totalCount,
              filteredCount: APIResponse.data.filteredCount,
              draw: APIResponse.data.draw,
            };
          else this.httpService.handleErrorMessage(APIResponse.status.message);
        }),
        catchError(this.httpService.handleError)
      );
  }

  submitChanges(
    documentsSPResponse: DocumentsSPReponse[],
    comments: string
  ): Observable<APIResponse> {
    let documents: DocumentsChangesRequest[] = documentsSPResponse.map(
      (doc) => ({
        id: doc.id,
        isActive: doc.active,
      })
    );

    return this.http
      .post<APIResponse>(
        this.httpService.buildUrl(
          this.MODULE,
          this.VERSION,
          this.RESOURCE,
          this.SUBMIT_CHANGES
        ),
        { documents, comment: comments },
        this.HTTP_OPTIONS
      )
      .pipe(catchError(this.httpService.handleError));
  }

  approveRejectDocuments(
    documentIds: number[],
    approve: boolean,
    comments: string
  ): Observable<APIResponse> {
    return this.http
      .post<APIResponse>(
        this.httpService.buildUrl(
          this.MODULE,
          this.VERSION,
          this.RESOURCE,
          this.APPROVE_REJECT
        ),
        { documentIds, approve, comment: comments },
        this.HTTP_OPTIONS
      )
      .pipe(catchError(this.httpService.handleError));
  }

  deleteDocument(documentId: number): Observable<APIResponse> {
    return this.http
      .delete<APIResponse>(
        this.httpService.buildUrl(
          this.MODULE,
          this.VERSION,
          this.RESOURCE,
          `${this.DELETE}/${documentId}`
        ),
        this.HTTP_OPTIONS
      )
      .pipe(
        map((APIResponse: APIResponse) => {
          if (APIResponse.status.success) return APIResponse.data;
          else {
            this.httpService.handleErrorMessage(APIResponse.status.message);
            throw new Error(APIResponse.status.message);
          }
        }),
        catchError(this.httpService.handleError)
      );
  }

  uploadDocuments(files: File[], userId: number, comments: string): Observable<any> {
    const formData: FormData = new FormData();

    files.forEach((file, idx) => {
      formData.append("files", file, file.name);
    });

    formData.append("comment", comments);

    // formData.append("uploaderUserId", userId.toString());

    return this.http
      .post<any>(
        this.httpService.buildUrl(
          this.MODULE,
          this.VERSION,
          this.RESOURCE,
          this.UPLOAD_DOCUMENTS
        ),
        formData
      )
      .pipe(
        map((APIResponse: APIResponse) => {
          if (APIResponse?.status?.success) {
            return APIResponse.data;
          } else {
            throw new Error(APIResponse?.status?.message || "Upload failed");
          }
        }),
        catchError(this.httpService.handleError)
      );
  }

  synchronizeDocuments(): Observable<APIResponse> {
    return this.http
      .post<APIResponse>(
        this.httpService.buildUrl(
          this.MODULE,
          this.VERSION,
          this.RESOURCE,
          this.SYNCHRONIZE_DOCUMENTS
        ),
        {},
        this.HTTP_OPTIONS
      )
      .pipe(
        map((APIResponse: APIResponse) => {
          if (APIResponse.status.success) return APIResponse.data;
          else {
            this.httpService.handleErrorMessage(APIResponse.status.message);
            throw new Error(APIResponse.status.message);
          }
        }),
        catchError(this.httpService.handleError)
      );
  }

  checkOverwrite(
    searchValue: string = "",
    contains: boolean = false
  ): Observable<{
    data: DocumentsSPReponse[];
    totalCount: number;
    filteredCount: number;
    draw: number;
  }> {
    const params: any = {
      searchValue: searchValue,
      draw: 1,
      start: 0,
      length: 1,
      onlyPendingDocuments: false,
      contains: contains,
    };

    const httpOptions = {
      params,
      headers: new HttpHeaders({
        "Skip-Loading": "true",
      }),
    };

    return this.http
      .get<APIResponse>(
        this.httpService.buildUrl(
          this.MODULE,
          this.VERSION,
          this.RESOURCE,
          this.GET_DOCUMENTS
        ),
        httpOptions
      )
      .pipe(
        map((APIResponse: APIResponse) => {
          if (APIResponse.status.success)
            return {
              data: APIResponse.data.data,
              totalCount: APIResponse.data.totalCount,
              filteredCount: APIResponse.data.filteredCount,
              draw: APIResponse.data.draw,
            };
          else this.httpService.handleErrorMessage(APIResponse.status.message);
        }),
        catchError(this.httpService.handleError)
      );
  }

  downloadDocument(documentName: string): Observable<any> {
    if (!documentName.trim()) return new Observable<any>();

    const httpOptions = {
      headers: new HttpHeaders({ "Content-Type": "application/json" }),
    };

    const body = {
      FileName: documentName,
      name: "",
      Thread: "",
      Line: "",
      User: "",
      time_zone: "",
    };


    return this.http.post<any>(
      environment.apiEndpoint + "/v1/IA/get-blob",
      JSON.stringify(body),
      httpOptions
    ).pipe(
      map((response: any) => {
        if (response?.status?.success === false) {
          throw new Error(response?.status?.message || "Download failed");
        }
        
        return response;
      }),
      catchError(this.httpService.handleError)
    );
  }
}
